import { useState } from 'react';
import { Wrench, Loader2, Eye, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, EmptyState, ErrorState } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@mustauto/ui/web';
import { SkeletonTable } from '@mustauto/ui/web';
import { useToolReports, type ToolReport } from '@/hooks/useToolReports';

type StatusFilter = 'open' | 'resolved' | undefined;

const ISSUE_LABELS: Record<string, string> = {
  broken: 'Quebrou',
  needs_repair: 'Precisa de reparo',
  needs_replacement: 'Substituir',
  missing: 'Sumiu',
};

const STATUS_BADGE: Record<string, { label: string; variant: 'warning' | 'info' | 'success' }> = {
  open: { label: 'Aberto', variant: 'warning' },
  acknowledged: { label: 'Ciente', variant: 'info' },
  resolved: { label: 'Resolvido', variant: 'success' },
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ToolReportsPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(undefined);
  const [resolveId, setResolveId] = useState<string | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState('');

  const filterForApi = statusFilter === 'open' ? 'open' as const : statusFilter === 'resolved' ? 'resolved' as const : undefined;
  const { reports, isLoading, isError, error, refetch, acknowledge, isAcknowledging, resolve, isResolving } =
    useToolReports({ status: filterForApi });

  const filteredReports = statusFilter === 'open'
    ? reports.filter((r) => r.status === 'open' || r.status === 'acknowledged')
    : reports;

  const handleAcknowledge = async (id: string) => {
    await acknowledge(id);
  };

  const handleResolve = async () => {
    if (!resolveId) return;
    await resolve({ id: resolveId, resolutionNotes: resolutionNotes.trim() || undefined });
    setResolveId(null);
    setResolutionNotes('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Wrench size={24} />
          Ferramentas — Problemas reportados
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Gerencie os problemas com ferramentas reportados pela equipe.
        </p>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setStatusFilter(statusFilter === 'open' ? undefined : 'open')}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            statusFilter === 'open'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          Abertos
        </button>
        <button
          onClick={() => setStatusFilter(statusFilter === 'resolved' ? undefined : 'resolved')}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            statusFilter === 'resolved'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          Resolvidos
        </button>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Reportes</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonTable rows={5} />
          ) : isError && reports.length === 0 ? (
            /* "Nenhum reporte encontrado" quando a busca falhou faz o gestor
               concluir que nenhuma ferramenta da oficina está quebrada. */
            <ErrorState title="Não foi possível carregar os reportes" error={error} onRetry={() => refetch()} />
          ) : filteredReports.length === 0 ? (
            <EmptyState
              icon={<Wrench size={28} />}
              title="Nenhum reporte encontrado"
              description="Mecânicos reportam ferramenta quebrada ou faltando pelo app. Os reportes aparecem aqui para você resolver."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ferramenta</TableHead>
                  <TableHead>Problema</TableHead>
                  <TableHead>Reportado por</TableHead>
                  <TableHead>OS</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReports.map((report) => {
                  const statusInfo = STATUS_BADGE[report.status] ?? STATUS_BADGE['open']!;
                  return (
                    <TableRow key={report.id}>
                      <TableCell className="font-medium">{report.toolName}</TableCell>
                      <TableCell>{ISSUE_LABELS[report.issueType] ?? report.issueType}</TableCell>
                      <TableCell>{report.reporterName}</TableCell>
                      <TableCell>
                        {report.osNumber ? `#${report.osNumber}` : '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(report.createdAt)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {report.status === 'open' && (
                            <button
                              onClick={() => handleAcknowledge(report.id)}
                              disabled={isAcknowledging}
                              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-950/60 transition-colors disabled:opacity-50"
                            >
                              <Eye size={14} />
                              Ciente
                            </button>
                          )}
                          {(report.status === 'open' || report.status === 'acknowledged') && (
                            <button
                              onClick={() => setResolveId(report.id)}
                              disabled={isResolving}
                              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:hover:bg-emerald-950/60 transition-colors disabled:opacity-50"
                            >
                              <CheckCircle size={14} />
                              Resolvido
                            </button>
                          )}
                          {report.resolutionNotes && (
                            <span className="text-xs text-muted-foreground italic max-w-[200px] truncate" title={report.resolutionNotes}>
                              {report.resolutionNotes}
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Resolve Modal */}
      {resolveId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background rounded-xl p-6 w-full max-w-md shadow-xl">
            <h2 className="text-lg font-bold mb-4">Resolver problema</h2>
            <label className="block text-sm font-medium text-foreground mb-2">
              Notas de resolução (opcional)
            </label>
            <textarea
              className="w-full rounded-lg border border-border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              rows={3}
              placeholder="Ex: Ferramenta substituída, nova unidade no armário 3..."
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => { setResolveId(null); setResolutionNotes(''); }}
                className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleResolve}
                disabled={isResolving}
                className="rounded-lg px-4 py-2 text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 transition-colors disabled:opacity-50"
              >
                {isResolving ? 'Salvando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
